import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from './Dashboard';
import { API } from '../App';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Edit, Users, Calendar, DollarSign, 
  TrendingUp, UserPlus, Activity, X, Search, Filter
} from 'lucide-react';

const ActivityManagement = () => {
  const [activeTab, setActiveTab] = useState('bookings');
  const [activities, setActivities] = useState([]);
  const [clients, setClients] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  
  // Modals
  const [showClientModal, setShowClientModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);

  // Forms
  const [clientForm, setClientForm] = useState({
    full_name: '', nationality: '', id_number: '', email: '', phone: '', notes: ''
  });
  const [bookingForm, setBookingForm] = useState({
    activity_id: '', client_type: 'independent', client_id: '', booking_date: '', 
    duration: '', price: 0, currency: 'EUR', payment_status: 'not_paid', payment_method: 'cash', notes: ''
  });
  const [expenseForm, setExpenseForm] = useState({
    activity_booking_id: '', description: '', amount: 0, currency: 'DT', date: ''
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const [activitiesRes, clientsRes, bookingsRes, expensesRes, reservationsRes, statsRes, countriesRes] = await Promise.all([
        axios.get(`${API}/activities`),
        axios.get(`${API}/independent-clients`),
        axios.get(`${API}/activity-bookings`),
        axios.get(`${API}/activity-expenses`),
        axios.get(`${API}/reservations`),
        axios.get(`${API}/activity-stats`),
        axios.get(`${API}/countries`)
      ]);
      setActivities(activitiesRes.data);
      setClients(clientsRes.data);
      setBookings(bookingsRes.data);
      setExpenses(expensesRes.data);
      setReservations(reservationsRes.data);
      setStats(statsRes.data);
      setCountries(countriesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Client CRUD
  const handleClientSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(`${API}/independent-clients/${editingClient.id}`, clientForm);
        toast.success('Client updated!');
      } else {
        await axios.post(`${API}/independent-clients`, clientForm);
        toast.success('Client created!');
      }
      setShowClientModal(false);
      setEditingClient(null);
      setClientForm({ full_name: '', nationality: '', id_number: '', email: '', phone: '', notes: '' });
      loadAllData();
    } catch (error) {
      toast.error('Failed to save client');
    }
  };

  const deleteClient = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await axios.delete(`${API}/independent-clients/${id}`);
      toast.success('Client deleted');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  // Booking CRUD
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBooking) {
        await axios.put(`${API}/activity-bookings/${editingBooking.id}`, bookingForm);
        toast.success('Booking updated!');
      } else {
        await axios.post(`${API}/activity-bookings`, bookingForm);
        toast.success('Booking created!');
      }
      setShowBookingModal(false);
      setEditingBooking(null);
      setBookingForm({
        activity_id: '', client_type: 'independent', client_id: '', booking_date: '', 
        duration: '', price: 0, currency: 'EUR', payment_status: 'not_paid', payment_method: 'cash', notes: ''
      });
      loadAllData();
    } catch (error) {
      toast.error('Failed to save booking');
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      await axios.delete(`${API}/activity-bookings/${id}`);
      toast.success('Booking deleted');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  // Expense CRUD
  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/activity-expenses`, expenseForm);
      toast.success('Expense added!');
      setShowExpenseModal(false);
      setExpenseForm({ activity_booking_id: '', description: '', amount: 0, currency: 'DT', date: '' });
      loadAllData();
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/activity-expenses/${id}`);
      toast.success('Expense deleted');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const openEditClient = (client) => {
    setEditingClient(client);
    setClientForm({
      full_name: client.full_name,
      nationality: client.nationality,
      id_number: client.id_number || '',
      email: client.email || '',
      phone: client.phone || '',
      notes: client.notes || ''
    });
    setShowClientModal(true);
  };

  const openEditBooking = (booking) => {
    setEditingBooking(booking);
    setBookingForm({
      activity_id: booking.activity_id,
      client_type: booking.client_type,
      client_id: booking.client_id,
      booking_date: booking.booking_date,
      duration: booking.duration || '',
      price: booking.price,
      currency: booking.currency,
      payment_status: booking.payment_status,
      payment_method: booking.payment_method,
      notes: booking.notes || ''
    });
    setShowBookingModal(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-xl">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-3xl font-bold text-gray-900">Activity Management</h2>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          {[
            { id: 'bookings', label: 'Bookings', icon: Calendar },
            { id: 'clients', label: 'Independent Clients', icon: Users },
            { id: 'expenses', label: 'Expenses', icon: DollarSign },
            { id: 'stats', label: 'Statistics', icon: TrendingUp }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition ${
                activeTab === tab.id 
                  ? 'bg-white text-blue-600 shadow' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setEditingBooking(null);
                  setBookingForm({
                    activity_id: '', client_type: 'independent', client_id: '', booking_date: '', 
                    duration: '', price: 0, currency: 'EUR', payment_status: 'not_paid', payment_method: 'cash', notes: ''
                  });
                  setShowBookingModal(true);
                }}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                <span>New Booking</span>
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {bookings.map(booking => (
                    <tr key={booking.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{booking.activity_name}</td>
                      <td className="px-4 py-3">{booking.client_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          booking.client_type === 'wostel' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {booking.client_type === 'wostel' ? 'Wostel' : 'Independent'}
                        </span>
                      </td>
                      <td className="px-4 py-3">{booking.booking_date}</td>
                      <td className="px-4 py-3 font-semibold">{booking.price} {booking.currency}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          booking.payment_status === 'paid' 
                            ? 'bg-green-100 text-green-800' 
                            : booking.payment_status === 'partial'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {booking.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex space-x-2">
                          <button onClick={() => openEditBooking(booking)} className="text-blue-600 hover:text-blue-800">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteBooking(booking.id)} className="text-red-600 hover:text-red-800">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bookings.length === 0 && (
                <div className="text-center py-8 text-gray-500">No bookings yet</div>
              )}
            </div>
          </div>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setEditingClient(null);
                  setClientForm({ full_name: '', nationality: '', id_number: '', email: '', phone: '', notes: '' });
                  setShowClientModal(true);
                }}
                className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
              >
                <UserPlus className="w-4 h-4" />
                <span>New Client</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map(client => (
                <div key={client.id} className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{client.full_name}</h3>
                      <p className="text-gray-500 text-sm">{client.nationality}</p>
                    </div>
                    <div className="flex space-x-1">
                      <button onClick={() => openEditClient(client)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteClient(client.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {client.email && <p className="text-sm text-gray-600">📧 {client.email}</p>}
                  {client.phone && <p className="text-sm text-gray-600">📞 {client.phone}</p>}
                  {client.id_number && <p className="text-sm text-gray-600">🪪 {client.id_number}</p>}
                </div>
              ))}
            </div>
            {clients.length === 0 && (
              <div className="text-center py-8 text-gray-500 bg-white rounded-xl">No independent clients yet</div>
            )}
          </div>
        )}

        {/* Expenses Tab */}
        {activeTab === 'expenses' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setExpenseForm({ activity_booking_id: '', description: '', amount: 0, currency: 'DT', date: '' });
                  setShowExpenseModal(true);
                }}
                className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
              >
                <Plus className="w-4 h-4" />
                <span>Add Expense</span>
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {expenses.map(expense => (
                    <tr key={expense.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">{expense.description}</td>
                      <td className="px-4 py-3 font-semibold text-red-600">-{expense.amount} {expense.currency}</td>
                      <td className="px-4 py-3">{expense.date}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => deleteExpense(expense.id)} className="text-red-600 hover:text-red-800">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {expenses.length === 0 && (
                <div className="text-center py-8 text-gray-500">No expenses yet</div>
              )}
            </div>
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Total Bookings</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_bookings}</p>
                  </div>
                  <Calendar className="w-10 h-10 text-blue-500" />
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Total Income</p>
                    <p className="text-3xl font-bold text-green-600">{stats.total_income_dt.toFixed(2)} DT</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-green-500" />
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Total Expenses</p>
                    <p className="text-3xl font-bold text-red-600">{stats.total_expenses_dt.toFixed(2)} DT</p>
                  </div>
                  <DollarSign className="w-10 h-10 text-red-500" />
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Net Profit</p>
                    <p className={`text-3xl font-bold ${stats.net_profit_dt >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {stats.net_profit_dt.toFixed(2)} DT
                    </p>
                  </div>
                  <Activity className="w-10 h-10 text-purple-500" />
                </div>
              </div>
            </div>

            {/* Income Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold mb-4">Income by Client Type</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Wostel Clients</span>
                    <span className="font-semibold text-blue-600">{stats.wostel_income_dt.toFixed(2)} DT</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Independent Clients</span>
                    <span className="font-semibold text-purple-600">{stats.independent_income_dt.toFixed(2)} DT</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold mb-4">Bookings by Client Type</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Wostel Clients</span>
                    <span className="font-semibold">{stats.wostel_bookings}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Independent Clients</span>
                    <span className="font-semibold">{stats.independent_bookings}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Income by Activity */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Income by Activity</h3>
              <div className="space-y-3">
                {Object.entries(stats.income_by_activity).map(([name, amount]) => (
                  <div key={name} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{name}</span>
                    <span className="font-semibold text-green-600">{amount.toFixed(2)} DT</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Client Modal */}
        {showClientModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">{editingClient ? 'Edit Client' : 'New Client'}</h3>
                <button onClick={() => setShowClientModal(false)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleClientSubmit} className="space-y-4">
                <input
                  type="text"
                  placeholder="Full Name *"
                  required
                  value={clientForm.full_name}
                  onChange={(e) => setClientForm({...clientForm, full_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <select
                  required
                  value={clientForm.nationality}
                  onChange={(e) => setClientForm({...clientForm, nationality: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select Nationality *</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="ID Number"
                  value={clientForm.id_number}
                  onChange={(e) => setClientForm({...clientForm, id_number: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={clientForm.email}
                  onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <input
                  type="text"
                  placeholder="Phone"
                  value={clientForm.phone}
                  onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <textarea
                  placeholder="Notes"
                  value={clientForm.notes}
                  onChange={(e) => setClientForm({...clientForm, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                />
                <button type="submit" className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700">
                  {editingClient ? 'Update Client' : 'Create Client'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Booking Modal */}
        {showBookingModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">{editingBooking ? 'Edit Booking' : 'New Booking'}</h3>
                <button onClick={() => setShowBookingModal(false)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleBookingSubmit} className="space-y-4">
                <select
                  required
                  value={bookingForm.activity_id}
                  onChange={(e) => setBookingForm({...bookingForm, activity_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select Activity *</option>
                  {activities.map(act => (
                    <option key={act.id} value={act.id}>{act.name} - {act.price} {act.currency}</option>
                  ))}
                </select>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Type</label>
                  <select
                    value={bookingForm.client_type}
                    onChange={(e) => setBookingForm({...bookingForm, client_type: e.target.value, client_id: ''})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="independent">Independent Client</option>
                    <option value="wostel">Wostel Guest</option>
                  </select>
                </div>

                {bookingForm.client_type === 'independent' ? (
                  <select
                    required
                    value={bookingForm.client_id}
                    onChange={(e) => setBookingForm({...bookingForm, client_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Select Client *</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.full_name} ({client.nationality})</option>
                    ))}
                  </select>
                ) : (
                  <select
                    required
                    value={bookingForm.client_id}
                    onChange={(e) => setBookingForm({...bookingForm, client_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Select Reservation *</option>
                    {reservations.map(res => (
                      <option key={res.id} value={res.id}>{res.guest_name} ({res.check_in_date})</option>
                    ))}
                  </select>
                )}

                <input
                  type="date"
                  required
                  value={bookingForm.booking_date}
                  onChange={(e) => setBookingForm({...bookingForm, booking_date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />

                <input
                  type="text"
                  placeholder="Duration (e.g., 2 hours, Full day)"
                  value={bookingForm.duration}
                  onChange={(e) => setBookingForm({...bookingForm, duration: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />

                <div className="flex space-x-2">
                  <input
                    type="number"
                    placeholder="Price"
                    required
                    value={bookingForm.price}
                    onChange={(e) => setBookingForm({...bookingForm, price: parseFloat(e.target.value) || 0})}
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                  <select
                    value={bookingForm.currency}
                    onChange={(e) => setBookingForm({...bookingForm, currency: e.target.value})}
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="DT">DT</option>
                  </select>
                </div>

                <div className="flex space-x-2">
                  <select
                    value={bookingForm.payment_status}
                    onChange={(e) => setBookingForm({...bookingForm, payment_status: e.target.value})}
                    className="flex-1 px-3 py-2 border rounded-lg"
                  >
                    <option value="not_paid">Not Paid</option>
                    <option value="partial">Partial</option>
                    <option value="paid">Paid</option>
                  </select>
                  <select
                    value={bookingForm.payment_method}
                    onChange={(e) => setBookingForm({...bookingForm, payment_method: e.target.value})}
                    className="flex-1 px-3 py-2 border rounded-lg"
                  >
                    <option value="cash">Cash</option>
                    <option value="online">Online</option>
                    <option value="card">Card</option>
                    <option value="transfer">Transfer</option>
                  </select>
                </div>

                <textarea
                  placeholder="Notes"
                  value={bookingForm.notes}
                  onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                />

                <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700">
                  {editingBooking ? 'Update Booking' : 'Create Booking'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Expense Modal */}
        {showExpenseModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Add Expense</h3>
                <button onClick={() => setShowExpenseModal(false)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleExpenseSubmit} className="space-y-4">
                <input
                  type="text"
                  placeholder="Description *"
                  required
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <div className="flex space-x-2">
                  <input
                    type="number"
                    placeholder="Amount"
                    required
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({...expenseForm, amount: parseFloat(e.target.value) || 0})}
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                  <select
                    value={expenseForm.currency}
                    onChange={(e) => setExpenseForm({...expenseForm, currency: e.target.value})}
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="DT">DT</option>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
                <input
                  type="date"
                  required
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm({...expenseForm, date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <select
                  value={expenseForm.activity_booking_id}
                  onChange={(e) => setExpenseForm({...expenseForm, activity_booking_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Link to Booking (optional)</option>
                  {bookings.map(booking => (
                    <option key={booking.id} value={booking.id}>
                      {booking.activity_name} - {booking.client_name} ({booking.booking_date})
                    </option>
                  ))}
                </select>
                <button type="submit" className="w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700">
                  Add Expense
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ActivityManagement;
